import type { RatioAssessment, RatioCard } from '../../../data/ServerApiClient'

const BENCHMARK_SCORE_EPS = 0.000001

export const ratioBenchmarkAssessmentForScore = (
  score: number,
  hasDirection = true,
): RatioAssessment => {
  if (!hasDirection || !Number.isFinite(score)) return 'neutral'
  return score + BENCHMARK_SCORE_EPS >= 1 ? 'healthy' : 'concern'
}

/** `RatioBenchmark.basis` when the server fell back to the company's own trailing average. */
export const COMPANY_HISTORY_BENCHMARK_BASIS = 'company_history'

export type RatioBenchmarkSourceKind = 'industry' | 'history' | 'band'
export type RatioBenchmarkSource = {
  kind: RatioBenchmarkSourceKind
  /** Short pill text for the benchmark column (e.g. "IRS", "9-mo avg", "band"). */
  tag: string
}

/**
 * The four fields the benchmark helpers below actually read. Typed as a structural subset rather
 * than the full `RatioCard` so leaner carriers of the same facts — the firm cockpit's
 * `FocusRatioSlice`, for one — can reuse this precedence logic instead of forking it. Every
 * `RatioCard` satisfies this, so existing call sites are unaffected.
 */
export type BenchmarkableRatio = Pick<
  RatioCard,
  'unit' | 'higher_is_better' | 'benchmark'
> & { trend: Array<{ period: string; value: number | null }> }

/**
 * Client-side mirror of the server's company-history fallback: the trailing
 * average of the card's prior finite months (up to 12, excluding the latest so
 * the current value isn't compared against itself). Used when the served card
 * carries no benchmark value AND no usable band — e.g. a cached response from
 * before the server started benchmarking short-history / negative-average
 * metrics. Any prior history at all produces an anchor; only a card with zero
 * prior finite months returns null.
 */
export const ratioHistoryFallbackBenchmark = (
  ratio: BenchmarkableRatio,
): { value: number; months: number } | null => {
  const trend = ratio.trend || []
  const prior = trend
    .slice(0, Math.max(trend.length - 1, 0))
    .map((point) => point.value)
    .filter((value): value is number => value != null && Number.isFinite(value))
  if (!prior.length) return null
  const window = prior.slice(-12)
  const value = window.reduce((sum, v) => sum + v, 0) / window.length
  if (!Number.isFinite(value)) return null
  return { value, months: window.length }
}

/**
 * Mirror of the server's percent-point band normalization (fpa.rs,
 * `build_one_definition_card`): Ratio-unit values are fractions, but AI-authored
 * bands often arrive in percent points ("12" meaning 12%), which display as
 * 1200%. A Ratio-unit edge past 150% is read as percent points. Idempotent once
 * the server serves corrected bands (0.12 stays 0.12).
 */
const normalizedBandEdge = (ratio: BenchmarkableRatio, edge: number | null | undefined): number | null => {
  if (edge == null || !Number.isFinite(edge)) return null
  if (ratio.unit === 'ratio' && Math.abs(edge) > 1.5) return edge / 100
  return edge
}

/**
 * The single value a ratio is benchmarked against. Precedence: a supplied
 * benchmark value (IRS industry average, or the server's own-history trailing
 * average — history anchors may legitimately be zero/negative for offset
 * metrics), then the healthy-band edge in the ratio's good direction, then the
 * client-computed trailing average. MUST stay in lockstep with
 * `ratioBenchmarkSource` below: both return null for exactly the same cards.
 */
export const ratioBenchmarkAnchor = (ratio: BenchmarkableRatio): number | null => {
  // Defensive optional access: frozen pin-time card copies may predate newer benchmark fields.
  const bench = ratio.benchmark || ({} as RatioCard['benchmark'])
  const value = bench.value
  if (value != null && Number.isFinite(value)) {
    // A supplied benchmark value anchors the comparison regardless of direction (higher_is_better
    // may be null for non-directional ratios like DPO). IRS averages are only trusted when
    // positive; own-history averages are trusted at any sign.
    if (bench.basis === COMPANY_HISTORY_BENCHMARK_BASIS || value > 0) return value
  }
  const min = normalizedBandEdge(ratio, bench.healthy_min)
  const max = normalizedBandEdge(ratio, bench.healthy_max)
  if (ratio.higher_is_better === true && min != null && min > 0) return min
  if (ratio.higher_is_better === false && max != null && max > 0) return max
  return ratioHistoryFallbackBenchmark(ratio)?.value ?? null
}

/**
 * Which source the row's benchmark anchor comes from. MUST mirror the anchor
 * precedence in `ratioBenchmarkAnchor` above. Returns null exactly when that
 * anchor is null (unbenchmarked — zero prior history and no external anchor).
 */
export const ratioBenchmarkSource = (ratio: BenchmarkableRatio): RatioBenchmarkSource | null => {
  const bench = ratio.benchmark || ({} as RatioCard['benchmark'])
  const value = bench.value
  if (value != null && Number.isFinite(value)) {
    if (bench.basis === COMPANY_HISTORY_BENCHMARK_BASIS) {
      return { kind: 'history', tag: bench.source_label || 'own avg' }
    }
    if (value > 0) return { kind: 'industry', tag: 'IRS' }
  }
  const min = bench.healthy_min
  const max = bench.healthy_max
  if (ratio.higher_is_better === true && min != null && Number.isFinite(min) && min > 0) {
    return { kind: 'band', tag: 'band' }
  }
  if (ratio.higher_is_better === false && max != null && Number.isFinite(max) && max > 0) {
    return { kind: 'band', tag: 'band' }
  }
  const fallback = ratioHistoryFallbackBenchmark(ratio)
  if (fallback) return { kind: 'history', tag: `${fallback.months}-mo avg` }
  return null
}

/**
 * Missing a benchmark against your own history is momentum, not ill health:
 * soften what the score math would call "concern" to "watch" for history-
 * benchmarked rows. Red stays reserved for external-standard misses.
 */
export const softenHistoryBenchmarkAssessment = (
  assessment: RatioAssessment,
  source: RatioBenchmarkSource | null,
): RatioAssessment =>
  source?.kind === 'history' && assessment === 'concern' ? 'watch' : assessment

/**
 * Empty-state note for a row that cannot be benchmarked at all. With the
 * trailing-average fallback accepting any prior history, the only unbenchmarked
 * cards are brand-new metrics with no prior months (self-heals next month) or
 * windows with no company value to compare.
 */
export const ratioNoBenchmarkNote = (ratio: BenchmarkableRatio): string => {
  const trend = ratio.trend || []
  const priorFinite = trend
    .slice(0, Math.max(trend.length - 1, 0))
    .filter((point) => point != null && Number.isFinite(point.value)).length
  if (priorFinite < 1) return 'no prior months yet'
  return 'no value for this window'
}
